import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  MessageSquare, 
  Search, 
  Shield, 
  LogOut, 
  ChevronLeft, 
  ChevronRight,
  Trash2,
  Eye,
  Loader2,
  X,
  AlertTriangle,
  User,
  Clock
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { ScrollArea } from "../../components/ui/scroll-area";
import { toast } from "sonner";

const API = "/api";

export default function AdminConversations() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Modal states
  const [viewConversation, setViewConversation] = useState(null);
  const [deleteConversation, setDeleteConversation] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const adminEmail = localStorage.getItem("admin_email");

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchConversations();
  }, [navigate, page, search]);

  const fetchConversations = async () => {
    setLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "25" });
      if (search) params.append("search", search);

      const response = await fetch(`${API}/admin/conversations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/login");
        return;
      }

      const data = await response.json();
      setConversations(data.conversations || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error("Failed to fetch conversations");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (conversationId) => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/admin/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setViewConversation(data);
    } catch (error) {
      toast.error("Failed to load conversation");
    }
  };

  const handleDelete = async () => {
    if (!deleteConversation) return;
    setActionLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API}/admin/conversations/${deleteConversation.conversation_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error("Delete failed");

      toast.success("Conversation deleted");
      setDeleteConversation(null);
      fetchConversations();
    } catch (error) {
      toast.error("Failed to delete conversation");
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/login");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Conversations</h1>
            <p className="text-[#666]">{total} total conversations</p>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
            <Input
              placeholder="Search by user email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 bg-[#111] border-[#333] text-white placeholder:text-[#555]"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0a0a0a] border-b border-[#222]">
                <tr>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Title</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">User</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Messages</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Created</th>
                  <th className="text-right text-[#888] text-sm font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-[#555] mx-auto" />
                    </td>
                  </tr>
                ) : conversations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-[#555]">No conversations found</td>
                  </tr>
                ) : (
                  conversations.map((conv) => (
                    <tr key={conv.conversation_id} className="border-b border-[#222] hover:bg-[#1a1a1a] transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-[#555]" />
                          <span className="text-white">{conv.title || "New Chat"}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white text-sm">{conv.user_name || "Unknown"}</p>
                          <p className="text-[#666] text-xs">{conv.user_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[#888]">{conv.message_count || 0}</td>
                      <td className="px-6 py-4 text-[#888] text-sm">{formatDate(conv.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(conv.conversation_id)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-400/10"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConversation(conv)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#222]">
              <p className="text-[#666] text-sm">Page {page} of {totalPages}</p>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="text-[#888] hover:text-white disabled:opacity-50">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="text-[#888] hover:text-white disabled:opacity-50">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* View Conversation Modal */}
      <Dialog open={!!viewConversation} onOpenChange={() => setViewConversation(null)}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-400" />
              {viewConversation?.title || "Conversation"}
            </DialogTitle>
            <DialogDescription className="text-[#888]">
              <span className="flex items-center gap-4 mt-2">
                <span className="flex items-center gap-1"><User className="w-3 h-3" /> {viewConversation?.user_email}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(viewConversation?.created_at)}</span>
              </span>
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[50vh] pr-4">
            <div className="space-y-4 py-4">
              {viewConversation?.messages?.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-lg px-4 py-3 ${
                    msg.role === "user" ? "bg-[#14A800] text-white" : "bg-[#1a1a1a] text-[#E8E8E8]"
                  }`}>
                    <p className="text-xs text-opacity-70 mb-1">{msg.role === "user" ? "User" : "ZOEY"}</p>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConversation} onOpenChange={() => setDeleteConversation(null)}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Conversation
            </DialogTitle>
            <DialogDescription className="text-[#888]">
              This will permanently delete this conversation and all its messages.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDeleteConversation(null)} className="text-[#888] hover:text-white">Cancel</Button>
            <Button onClick={handleDelete} disabled={actionLoading} className="bg-red-600 hover:bg-red-700">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
